/**
 * Hook for reading agent-step metadata from a finalized session's metadata.json.
 *
 * The replay equivalent of {@link useAgentStream}: same return shape so
 * `<BrowserStepsViewer>` can swap data sources without other changes.
 *
 * Reads from the shared {@link useRecordingMetadata} React Query cache, so
 * a parallel `BrowserVideoView` mount that already fetched metadata.json
 * doesn't trigger a second HTTP request — the queryKey dedupes it.
 *
 * @module browser-automation/hooks/use-metadata-steps
 */

import { useMemo } from 'react';

import { EAgentStatus, type IAgentStep } from '../types';
import { useRecordingMetadata } from './use-recording-metadata';

/**
 * Return value of the useMetadataSteps hook. Matches IUseAgentStreamReturn
 * field-for-field so callers can treat the two interchangeably.
 */
export interface IUseMetadataStepsReturn {
  steps: IAgentStep[];
  agentStatus: EAgentStatus | null;
  isConnecting: boolean;
  isConnected: boolean;
  error: string | null;
}

/**
 * Pull the steps array from metadata.json at the session's artifacts base URL.
 *
 * Shares its underlying fetch with {@link useRecordingMetadata}, so when
 * `BrowserVideoView` is also mounted against the same `artifactsBase` only
 * one HTTP request is made.
 *
 * @param artifactsBase - Resolved artifacts URL (output of `resolveArtifactsBaseUrl`). Null skips the fetch.
 */
export function useMetadataSteps(
  artifactsBase: string | null,
): IUseMetadataStepsReturn {
  const query = useRecordingMetadata(artifactsBase);

  return useMemo<IUseMetadataStepsReturn>(() => {
    if (artifactsBase === null) {
      return {
        steps: [],
        agentStatus: null,
        isConnecting: false,
        isConnected: false,
        error: null,
      };
    }
    if (query.isLoading) {
      return {
        steps: [],
        agentStatus: null,
        isConnecting: true,
        isConnected: false,
        error: null,
      };
    }
    if (query.error) {
      return {
        steps: [],
        agentStatus: null,
        isConnecting: false,
        isConnected: false,
        error: query.error.message,
      };
    }
    const meta = query.data;
    if (meta === null || meta === undefined) {
      // 404 — finalize hasn't happened yet (or the recording was zero-action).
      // Match the previous behavior: no steps, IDLE, connected.
      return {
        steps: [],
        agentStatus: EAgentStatus.IDLE,
        isConnecting: false,
        isConnected: true,
        error: null,
      };
    }
    const steps: IAgentStep[] = (meta.steps ?? []).map(s => ({
      step_number: s.step_number,
      description: s.description,
      timestamp: s.timestamp ?? meta.started_at ?? '',
      video_offset_seconds: s.video_offset_seconds,
      success: s.success,
      action_type: s.action_type,
    }));
    return {
      steps,
      agentStatus: EAgentStatus.COMPLETED,
      isConnecting: false,
      isConnected: true,
      error: null,
    };
  }, [artifactsBase, query.isLoading, query.error, query.data]);
}
